import {
  Component,
  createContext,
  useContext,
  type ReactNode,
  type ErrorInfo,
} from "react"
import { ErrorFallback } from "./error-fallback"

interface ErrorBoundaryContextValue {
  resetErrorBoundary: () => void
}

const ErrorBoundaryContext = createContext<ErrorBoundaryContextValue | null>(
  null
)

export function useErrorBoundary() {
  const context = useContext(ErrorBoundaryContext)
  if (!context) {
    throw new Error("useErrorBoundary must be used within an ErrorBoundary")
  }
  return context
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  onReset?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Error caught by boundary:", error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.()
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <ErrorFallback
            description={this.state.error?.message}
            onRetry={this.resetErrorBoundary}
          />
        )
      )
    }

    return (
      <ErrorBoundaryContext.Provider
        value={{ resetErrorBoundary: this.resetErrorBoundary }}
      >
        {this.props.children}
      </ErrorBoundaryContext.Provider>
    )
  }
}
